import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Cpu,
  Gauge,
  MemoryStick,
  RefreshCw,
  Server,
  Thermometer,
} from 'lucide-react'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Progress,
  TimeSeriesChart,
} from '@agion/ui'
import { cn } from '@/core/analytics_utils'
import { getRequiredConfig } from '@/core/analytics_runtime_env'
import {
  GPU_CAPABILITIES,
  createEmptyGpuDashboard,
  loadGpuDashboard,
  type GpuDashboardSnapshot,
  type GpuEnvironmentMetrics,
} from './gpu_metrics_data'
import { gpuTelemetryEnvironmentForDeployment } from './gpu_environment'

const STALE_AFTER_SECONDS = 120

function formatNumber(value: number | null, digits: number): string {
  if (value === null) return '—'
  return value.toLocaleString(undefined, { maximumFractionDigits: digits })
}

function formatGiB(mebibytes: number | null): string {
  return mebibytes === null ? '—' : `${formatNumber(mebibytes / 1024, 1)} GiB`
}

function formatRate(value: number | null): string {
  if (value === null) return '—'
  if (value < 0.1) return `${value.toFixed(3)}/s`
  return `${value.toFixed(1)}/s`
}

function formatLatency(seconds: number | null): string {
  if (seconds === null) return '—'
  return seconds < 1 ? `${Math.round(seconds * 1000)} ms` : `${seconds.toFixed(2)} s`
}

function isStale(metrics: GpuEnvironmentMetrics, nowUnix: number): boolean {
  return metrics.lastSampleUnix !== null && nowUnix - metrics.lastSampleUnix > STALE_AFTER_SECONDS
}

function environmentState(
  metrics: GpuEnvironmentMetrics,
  stale: boolean,
  healthIncomplete: boolean,
): 'healthy' | 'degraded' | 'unavailable' | 'stale' {
  if (stale) return 'stale'
  if (metrics.backendUp === null && metrics.dcgmUp === null && metrics.ready === null) return 'unavailable'
  if (healthIncomplete) return 'degraded'
  if (
    metrics.backendUp
    && metrics.dcgmUp
    && metrics.ready
    && metrics.framebufferFreeMiB !== null
    && metrics.hardwareHealth !== null
    && metrics.hardwareHealth < 10
    && metrics.lastSampleUnix !== null
    && metrics.alerts.length === 0
  ) return 'healthy'
  return 'degraded'
}

const stateStyles = {
  healthy: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
  degraded: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300',
  unavailable: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300',
  stale: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
} as const

const stateBars = {
  healthy: 'bg-emerald-500',
  degraded: 'bg-rose-500',
  unavailable: 'bg-slate-300',
  stale: 'bg-amber-500',
} as const

function StatusDot({ value }: { value: boolean | null }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'h-2.5 w-2.5 rounded-full',
        value === true && 'bg-emerald-500',
        value === false && 'bg-rose-500',
        value === null && 'bg-slate-300 dark:bg-slate-600',
      )}
    />
  )
}

interface MetricTileProps {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
  detail?: string
  tone: 'blue' | 'emerald' | 'amber' | 'violet'
}

const metricTone = {
  blue: 'bg-blue-500',
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  violet: 'bg-violet-500',
} as const

function MetricTile({ icon: Icon, label, value, detail, tone }: MetricTileProps) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 px-4 py-4">
        <div className={cn('shrink-0 rounded-lg p-2.5', metricTone[tone])}>
          <Icon className="h-5 w-5 text-white" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
          <p className="text-xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">{value}</p>
          {detail && <p className="text-xs text-slate-400 dark:text-slate-500">{detail}</p>}
        </div>
      </CardContent>
    </Card>
  )
}

function targetLabel(value: boolean | null, t: (key: string, fallback: string) => string): string {
  if (value === true) return t('gpu.up', 'Up')
  if (value === false) return t('gpu.down', 'Down')
  return t('gpu.unknown', 'Unknown')
}

function EnvironmentPanel({
  metrics,
  nowUnix,
  healthIncomplete,
}: {
  metrics: GpuEnvironmentMetrics
  nowUnix: number
  healthIncomplete: boolean
}) {
  const { t } = useTranslation('pages')
  const stale = isStale(metrics, nowUnix)
  const state = environmentState(metrics, stale, healthIncomplete)
  const { framebufferFreeMiB, framebufferUsedMiB, framebufferReservedMiB } = metrics
  const totalFramebuffer = framebufferFreeMiB !== null
    && framebufferUsedMiB !== null
    && framebufferReservedMiB !== null
    ? framebufferFreeMiB + framebufferUsedMiB + framebufferReservedMiB
    : null
  const usedPercent = framebufferUsedMiB !== null && totalFramebuffer !== null && totalFramebuffer > 0
    ? (framebufferUsedMiB / totalFramebuffer) * 100
    : null
  const environmentLabel = metrics.environment === 'prod'
    ? t('gpu.production', 'Production')
    : t('gpu.development', 'Development')
  const stateLabel = {
    healthy: t('gpu.healthy', 'Healthy'),
    degraded: t('gpu.degraded', 'Degraded'),
    unavailable: t('gpu.noData', 'No data'),
    stale: t('gpu.stale', 'Stale'),
  }[state]

  return (
    <section aria-labelledby={`gpu-${metrics.environment}-title`} className="min-w-0 space-y-4">
      <Card className="overflow-hidden">
        <div className={cn('h-1', stateBars[state])} />
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2.5">
                <CardTitle id={`gpu-${metrics.environment}-title`} className="text-lg">{environmentLabel}</CardTitle>
                <Badge className={cn('border', stateStyles[state])}>{stateLabel}</Badge>
              </div>
              <p className="mt-1 font-mono text-xs text-slate-400 dark:text-slate-500">
                {metrics.instanceId ?? t('gpu.instanceUnknown', 'Instance not observed')}
              </p>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
              <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                <StatusDot value={metrics.backendUp} />
                {t('gpu.statusJoin', '{{label}} · {{status}}', { label: t('gpu.backendTarget', 'Backend'), status: targetLabel(metrics.backendUp, t) })}
              </span>
              <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                <StatusDot value={metrics.dcgmUp} />
                {t('gpu.statusJoin', '{{label}} · {{status}}', { label: t('gpu.hardwareTarget', 'Hardware'), status: targetLabel(metrics.dcgmUp, t) })}
              </span>
              <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                <StatusDot value={metrics.ready} />
                {t('gpu.statusJoin', '{{label}} · {{status}}', { label: t('gpu.modelReadiness', 'Models'), status: targetLabel(metrics.ready, t) })}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,15rem),1fr))] gap-3">
            <MetricTile
              icon={MemoryStick}
              label={t('gpu.vramFree', 'VRAM free')}
              value={formatGiB(metrics.framebufferFreeMiB)}
              detail={totalFramebuffer !== null && totalFramebuffer > 0 ? t('gpu.vramTotal', '{{total}} total', { total: formatGiB(totalFramebuffer) }) : undefined}
              tone="violet"
            />
            <MetricTile
              icon={Gauge}
              label={t('gpu.gpuUtilization', 'GPU utilization')}
              value={metrics.gpuUtilizationPct === null ? '—' : `${formatNumber(metrics.gpuUtilizationPct, 0)}%`}
              detail={t('gpu.memoryUtilization', '{{value}} memory I/O', { value: metrics.memoryUtilizationPct === null ? '—' : `${formatNumber(metrics.memoryUtilizationPct, 0)}%` })}
              tone="blue"
            />
            <MetricTile
              icon={Thermometer}
              label={t('gpu.temperature', 'Temperature')}
              value={metrics.temperatureCelsius === null ? '—' : `${formatNumber(metrics.temperatureCelsius, 0)} °C`}
              detail={metrics.powerWatts === null ? undefined : t('gpu.powerDraw', '{{value}} W power', { value: formatNumber(metrics.powerWatts, 0) })}
              tone="amber"
            />
            <MetricTile
              icon={Activity}
              label={t('gpu.throughput', 'Throughput')}
              value={formatRate(metrics.throughput)}
              detail={t('gpu.successfulRequests', 'Successful model requests')}
              tone="emerald"
            />
          </div>

          {usedPercent !== null && (
            <div>
              <div className="mb-2 flex justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>{t('gpu.framebufferUse', 'Framebuffer use')}</span>
                <span className="tabular-nums">{usedPercent.toFixed(1)}%</span>
              </div>
              <Progress
                value={Math.min(usedPercent, 100)}
                variant={metrics.framebufferFreeMiB !== null && metrics.framebufferFreeMiB < 1024 ? 'error' : metrics.framebufferFreeMiB !== null && metrics.framebufferFreeMiB < 4096 ? 'warning' : 'success'}
                aria-label={t('gpu.framebufferUsePercent', 'Framebuffer use {{value}} percent', { value: usedPercent.toFixed(1) })}
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('gpu.failures10m', 'Failures · 10m')}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">{formatNumber(metrics.failures, 0)}</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('gpu.rejections10m', 'Rejections · 10m')}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">{formatNumber(metrics.rejections, 0)}</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('gpu.sessionResets1h', 'Session resets · 1h')}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">{formatNumber(metrics.sessionResets, 0)}</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('gpu.processRestarts1h', 'Process restarts · 1h')}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">{formatNumber(metrics.recoveries, 0)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('gpu.capabilities', 'Model capabilities')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            {/* eslint-disable-next-line no-restricted-syntax */}
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('gpu.capability', 'Capability')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('gpu.rate5m', 'Rate · 5m')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('gpu.failures10m', 'Failures · 10m')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('gpu.lastLatency', 'Last latency')}</th>
                </tr>
              </thead>
              <tbody>
                {GPU_CAPABILITIES.map(capability => {
                  const capabilityMetrics = metrics.capabilities[capability]
                  return (
                    <tr key={capability} className="border-b border-slate-100 last:border-0 dark:border-slate-700/50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">{t(`gpu.capabilityNames.${capability}`, capability)}</td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-600 dark:text-slate-300">{formatRate(capabilityMetrics.throughput)}</td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-600 dark:text-slate-300">{formatNumber(capabilityMetrics.failures, 0)}</td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-600 dark:text-slate-300">{formatLatency(capabilityMetrics.latencySeconds)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {metrics.alerts.length > 0 && (
        <Alert variant="error">
          <AlertTitle>{t('gpu.firingAlerts', 'Firing GPU alerts')}</AlertTitle>
          <div className="mt-2 space-y-2">
            {metrics.alerts.map(alert => (
              <div key={alert.name} className="flex flex-wrap items-center gap-2">
                <Badge variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}>{alert.severity}</Badge>
                <span className="font-mono text-xs">{alert.name}</span>
              </div>
            ))}
          </div>
        </Alert>
      )}
    </section>
  )
}

function TrendCard({
  title,
  description,
  field,
  metrics,
  formatY,
  thresholds,
}: {
  title: string
  description: string
  field: 'framebufferSeries' | 'throughputSeries'
  metrics: GpuEnvironmentMetrics
  formatY: (value: number) => string
  thresholds?: Array<{ value: number; color: string; label: string }>
}) {
  const { t } = useTranslation('pages')
  const data = metrics[field].length > 0 ? [{
    label: metrics.environment === 'prod' ? t('gpu.production', 'Production') : t('gpu.development', 'Development'),
    color: metrics.environment === 'prod' ? 'var(--chart-2)' : 'var(--chart-1)',
    values: metrics[field].map(point => ({ date: point.time, value: point.value })),
  }] : []

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
        <p className="text-xs text-slate-500 dark:text-slate-400">{description}</p>
      </CardHeader>
      <CardContent>
        {data.length > 0 ? (
          <TimeSeriesChart
            data={data}
            height={240}
            showDots={false}
            formatX={date => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            formatY={formatY}
            thresholds={thresholds}
          />
        ) : (
          <div className="flex h-48 items-center justify-center text-sm text-slate-400 dark:text-slate-500">{t('gpu.noData', 'No data')}</div>
        )}
      </CardContent>
    </Card>
  )
}

export const GpuPage: React.FC = () => {
  const { t } = useTranslation('pages')
  const deploymentEnvironment = getRequiredConfig('VITE_ENVIRONMENT', 'development')
  const gpuEnvironment = gpuTelemetryEnvironmentForDeployment(deploymentEnvironment)
  const [snapshot, setSnapshot] = useState<GpuDashboardSnapshot>(() => createEmptyGpuDashboard(gpuEnvironment))
  const snapshotRef = useRef(snapshot)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [failedQueries, setFailedQueries] = useState<string[]>([])
  const lifecycleController = useRef<AbortController | null>(null)
  const inFlight = useRef<object | null>(null)

  const refresh = useCallback(async (signal: AbortSignal) => {
    if (signal.aborted || inFlight.current) return
    const request = {}
    inFlight.current = request
    setRefreshing(true)
    try {
      const result = await loadGpuDashboard(
        gpuEnvironment,
        snapshotRef.current.refreshedAt > 0 ? snapshotRef.current : undefined,
        new Date(),
        signal,
      )
      if (signal.aborted || inFlight.current !== request) return
      snapshotRef.current = result.snapshot
      setSnapshot(result.snapshot)
      setFailedQueries(result.failedQueries)
    } finally {
      if (inFlight.current === request) {
        inFlight.current = null
        if (!signal.aborted) {
          setLoading(false)
          setRefreshing(false)
        }
      }
    }
  }, [gpuEnvironment])

  useEffect(() => {
    const controller = new AbortController()
    lifecycleController.current = controller
    void refresh(controller.signal)
    const interval = window.setInterval(() => { void refresh(controller.signal) }, 30000)
    return () => {
      window.clearInterval(interval)
      controller.abort()
      if (lifecycleController.current === controller) lifecycleController.current = null
      inFlight.current = null
    }
  }, [refresh])

  const nowUnix = Date.now() / 1000
  const stale = isStale(snapshot.metrics, nowUnix)
  const unavailable = snapshot.metrics.lastSampleUnix === null
  const healthIncomplete = failedQueries.some(query => (
    query === 'instant:targets'
    || query === 'instant:freshness'
    || query === 'instant:gauges'
    || query === 'instant:alerts'
  ))
  const sourceHealthy = failedQueries.length === 0 && !stale && !unavailable
  const updatedLabel = snapshot.refreshedAt > 0
    ? new Date(snapshot.refreshedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—'

  return (
    <div className="container-standard min-w-0 px-4 sm:px-6">
      <div className="page-header flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="rounded-xl bg-violet-100 p-2.5 dark:bg-violet-950/60">
            <Cpu className="h-6 w-6 text-violet-700 dark:text-violet-300" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h1 className="page-title break-words text-2xl sm:text-3xl">{t('gpu.title', 'GPU model service')}</h1>
            <p className="page-description">{t('gpu.description', 'Model readiness, traffic, recovery, and NVIDIA hardware health')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-xs text-slate-400 dark:text-slate-500">{t('gpu.lastRefresh', 'Last refresh')}</p>
            <p className="text-sm font-medium tabular-nums text-slate-700 dark:text-slate-300">{updatedLabel}</p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              const controller = lifecycleController.current
              if (controller) void refresh(controller.signal)
            }}
            disabled={refreshing}
          >
            <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} aria-hidden="true" />
            {t('gpu.refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      {failedQueries.length > 0 && (
        <Alert variant="warning" className="mb-5">
          <AlertTitle>{t('gpu.partialTitle', 'Some GPU metrics could not be refreshed')}</AlertTitle>
          <AlertDescription>
            {t('gpu.partialDescription', 'Available signals and the last successful values remain visible. {{count}} query groups failed.', { count: failedQueries.length })}
          </AlertDescription>
        </Alert>
      )}

      {(stale || unavailable) && (
        <Alert variant="warning" className="mb-5">
          <AlertTitle>{t('gpu.staleTitle', 'GPU telemetry is unavailable or stale')}</AlertTitle>
          <AlertDescription>{t('gpu.staleDescription', 'The registered GPU environment has no current sample or its latest sample is more than two minutes old. Treat displayed values as historical evidence.')}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="min-w-0" aria-label={t('common.loading', 'Loading...')}>
          <Card><CardContent className="py-6"><div className="h-72 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" /></CardContent></Card>
        </div>
      ) : (
        <div className="min-w-0">
          <EnvironmentPanel
            metrics={snapshot.metrics}
            nowUnix={nowUnix}
            healthIncomplete={healthIncomplete}
          />
        </div>
      )}

      {!loading && (
        <section className="mt-8" aria-labelledby="gpu-trends-title">
          <div className="mb-4 flex items-center gap-2">
            <Server className="h-5 w-5 text-slate-500" aria-hidden="true" />
            <h2 id="gpu-trends-title" className="section-title mb-0">{t('gpu.sixHourTrends', 'Six-hour trends')}</h2>
          </div>
          <div className="grid min-w-0 grid-cols-1 gap-5 xl:grid-cols-2">
            <TrendCard
              title={t('gpu.framebufferHeadroom', 'Framebuffer headroom')}
              description={t('gpu.framebufferHeadroomDescription', 'Free GPU memory; warning and critical alert thresholds are shown.')}
              field="framebufferSeries"
              metrics={snapshot.metrics}
              formatY={value => `${(value / 1024).toFixed(1)} GiB`}
              thresholds={[
                { value: 4096, color: '#f59e0b', label: '4 GiB' },
                { value: 1024, color: '#e11d48', label: '1 GiB' },
              ]}
            />
            <TrendCard
              title={t('gpu.requestThroughput', 'Successful request throughput')}
              description={t('gpu.requestThroughputDescription', 'Five-minute request rate across all model capabilities.')}
              field="throughputSeries"
              metrics={snapshot.metrics}
              formatY={value => `${value.toFixed(1)}/s`}
            />
          </div>
        </section>
      )}

      <div className="mt-5 flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
        {sourceHealthy
          ? <CheckCircle2 data-testid="gpu-source-healthy" className="h-4 w-4 text-emerald-500" aria-hidden="true" />
          : <AlertTriangle data-testid="gpu-source-warning" className="h-4 w-4 text-amber-500" aria-hidden="true" />}
        <span>{t('gpu.sourceNote', 'Source: audited platform observability queries. Refreshes every 30 seconds.')}</span>
      </div>
    </div>
  )
}
